import ElementLoader from "components/ElementLoader";
import { Icon } from "components/Icon/Icon";
import { Fragment, MouseEventHandler, useMemo, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import "./Button.scss";

export const variations = [
    "primary",
    "dark",
    "white",
    "danger",
    "clear",
    "icon",
    "link",
] as const;

export type ButtonVariation = (typeof variations)[number];
export type ButtonSize = "small" | "default";

interface ButtonProps {
    label?: string;
    labelOnlyAria?: boolean;
    ariaLabel?: string;
    icon?: any;
    loading?: boolean;
    image?: any;
    variation?: ButtonVariation;
    className?: string;
    size?: ButtonSize;
    disabled?: boolean;
    href?: string;
    submit?: boolean;
    form?: string;
    onClick?: MouseEventHandler<any>;
    external?: boolean;
    tabIndex?: number;
    title?: string;
    autoFocus?: boolean;
    target?: string;
}

export const Button = (props: ButtonProps) => {
    const buttonRef = useRef(null);

    const buttonIcon = useMemo(() => {
        const { label = "", labelOnlyAria, icon, loading, image, variation } = props;
        if (icon || image) {
            return (
                <Fragment>
                    {loading ? (
                        variation === "icon" ? (
                            // Icon-only buttons keep the icon's box while loading,
                            // so the spinner does not resize the button (or its row).
                            <span className="button__loader-box" data-testid="button-loader-box">
                                <ElementLoader />
                            </span>
                        ) : (
                            <ElementLoader />
                        )
                    ) : (
                        image ?? <Icon className="button__icon" icon={icon} />
                    )}
                    {label !== "" && !labelOnlyAria && (
                        <span className="button__label">{label}</span>
                    )}
                </Fragment>
            );
        }

        if (loading && !icon) {
            return (
                <Fragment>
                    <ElementLoader />{" "}
                    {!labelOnlyAria && <span className="button__label">{label}</span>}
                </Fragment>
            );
        }

        if (!labelOnlyAria) {
            return label;
        }

        return null;
    }, [props]);

    const buttonClassName = useMemo(() => {
        const { variation = "dark", className, size, disabled, loading } = props;

        let buttonClassName = `button ${className || ""}`;

        const setAttributeClassName = ({ condition, value }) => {
            if (condition) {
                buttonClassName = `${buttonClassName} button--${value}`;
            } else {
                buttonClassName = buttonClassName.replace(` button--${value}`, "");
            }
        };
        variations.forEach((value) =>
            setAttributeClassName({ condition: variation === value, value })
        );

        // Size. The `large` visual (used only by the auth-screen submit buttons)
        // is applied via `className="button--large"`, not this prop.
        if (size && size === "small") {
            buttonClassName = `${buttonClassName} button--small`;
        }

        setAttributeClassName({ condition: disabled === true, value: "disabled" });
        setAttributeClassName({ condition: loading === true, value: "loading" });

        return buttonClassName;
    }, [props]);

    const renderedElement = useMemo(() => {
        const {
            href,
            disabled,
            loading,
            submit,
            form,
            label,
            ariaLabel: buttonAriaLabel,
            onClick,
            external,
            tabIndex,
            title,
            autoFocus,
            target,
        } = props;

        // generate aria label string
        const ariaLabel = buttonAriaLabel || label || "";

        if (href && href !== "") {
            if (external) {
                if (target) {
                    return (
                        <a
                            href={href}
                            rel="noopener noreferrer"
                            aria-label={ariaLabel}
                            className={buttonClassName}
                            ref={buttonRef}
                            onClick={onClick}
                            title={title}
                            target={target}
                        >
                            {buttonIcon}
                        </a>
                    );
                } else {
                    return (
                        <NavLink
                            to={href}
                            className={buttonClassName}
                            onClick={onClick}
                            rel="noopener noreferrer"
                        >
                            {buttonIcon}
                        </NavLink>
                    );
                }
            }

            return (
                <Link
                    to={href}
                    aria-label={ariaLabel}
                    className={buttonClassName}
                    ref={buttonRef}
                    onClick={onClick}
                    title={title}
                >
                    {buttonIcon}
                </Link>
            );
        }

        const submitButtonProps = submit ? { type: "submit" as "submit", form } : {};

        return (
            <button
                type={"button" as "button"}
                aria-label={ariaLabel}
                className={buttonClassName}
                disabled={disabled || loading}
                ref={buttonRef}
                onClick={onClick}
                title={title}
                autoFocus={autoFocus}
                tabIndex={tabIndex}
                {...submitButtonProps}
            >
                {buttonIcon}
            </button>
        );
    }, [buttonClassName, buttonIcon, props]);

    return renderedElement;
};
